import {
	Button,
	ContextMenu,
	ContextMenuDeleteItem,
	ContextMenuLinkItem,
	IDataListColumn,
	Id,
	Loading,
	PaginatedAutoDataList,
	Trigger,
	isSuccess,
	laravelPaginatedListFetchConfig,
	useToasts,
} from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useContactListUtils } from "../contacts/useContactListUtils";
import { LocalContactViewProvider } from "../contactViews/local/LocalContactViewProvider";
import { LocalContactViewSwitcher } from "../contactViews/local/LocalContactViewSwitcher";
import { distributionListService } from "../shared/api/distributionListService";
import { useDetailsPageEntityLoader } from "../shared/hooks";
import { useContactView } from "../shared/hooks/useContactView";
import { IContactIndexResource, IDistributionListUpdateResource } from "../shared/types";
import { DistributionListContactsDrawer } from "./DistributionListContactsDrawer";

export const DistributionListViewPage: FC = () => {
	const { data, isLoading } = useDetailsPageEntityLoader(distributionListService);

	if (isLoading || !data) {
		return <Loading />;
	}
	return (
		<LocalContactViewProvider contactViewKey="distributionListContactView">
			<DistributionListContactsList distributionList={data} />
		</LocalContactViewProvider>
	);
};

const DistributionListContactsList: FC<{ distributionList: IDistributionListUpdateResource }> = ({
	distributionList,
}) => {
	const { t } = useTranslation();
	const { showToast } = useToasts();
	const { currentContactView, isLoading } = useContactView();
	const { columns } = useContactListUtils(currentContactView);
	const queryClient = useQueryClient();

	const onSubmit = useCallback(
		async (contactIds: Id[]) => {
			const res = await distributionListService.syncContacts(distributionList.id, { contactIds });
			if (isSuccess(res)) {
				showToast({
					body: t("Contacts were updated."),
					title: t("Contacts"),
					type: "success",
				});
				void queryClient.invalidateQueries({
					queryKey: distributionListService.indexContactsKey(distributionList.id),
				});
				void queryClient.invalidateQueries({ queryKey: distributionListService.showKey(distributionList.id) });
				return true;
			}
			return false;
		},
		[distributionList.id, queryClient, showToast, t],
	);

	const onDelete = useCallback(
		async (contactId: Id) => {
			const res = await distributionListService.detachContacts(distributionList.id, { contactIds: [contactId] });
			if (isSuccess(res)) {
				showToast({
					body: t("Contact was removed."),
					title: t("Contact"),
					type: "success",
				});
				void queryClient.invalidateQueries({
					queryKey: distributionListService.indexContactsKey(distributionList.id),
				});
				void queryClient.invalidateQueries({ queryKey: distributionListService.showKey(distributionList.id) });
				return true;
			}
			return false;
		},
		[distributionList.id, queryClient, showToast, t],
	);

	const dataListColumns: IDataListColumn<IContactIndexResource>[] = useMemo(
		() => [
			...(columns as IDataListColumn<IContactIndexResource>[]),
			{
				heading: "",
				type: "actions",
				size: "max-content",
				action: ({ id }) => (
					<ContextMenu displayShortcutsAs="icon">
						<ContextMenuLinkItem
							to={`/contacts/active/${id}`}
							title={t("View contact")}
							label={t("View contact")}
							icon="eye"
							shortcut="primary"
						/>
						<ContextMenuDeleteItem
							action={() => onDelete(id)}
							title={t("Remove contact")}
							label={t("Remove contact")}
							heading={t("Remove from distribution list")}
							labels={{ close: t("Close"), confirm: t("Confirm") }}
						>
							{t("Are you sure you want to remove this contact from the distribution list?")}
						</ContextMenuDeleteItem>
					</ContextMenu>
				),
			},
		],
		[columns, onDelete, t],
	);

	if (isLoading) {
		return <Loading />;
	}
	return (
		<PaginatedAutoDataList
			name="distributionListContacts"
			columns={dataListColumns}
			listFetchConfig={laravelPaginatedListFetchConfig(
				[...distributionListService.indexContactsKey(distributionList.id), `${currentContactView?.id}`],
				distributionListService.indexContacts(distributionList.id, currentContactView?.id || null),
			)}
			heading={t("Contacts")}
			headerActions={
				<>
					<LocalContactViewSwitcher />
					<Trigger
						render={(props) => (
							<DistributionListContactsDrawer
								{...props}
								contactIds={distributionList.contactIds}
								onSubmit={(contactIds) => onSubmit(contactIds).then((isOk) => isOk && props.handleClose())}
							/>
						)}
					>
						{(props) => (
							<Button {...props} color="primary">
								{t("Manage contacts")}
							</Button>
						)}
					</Trigger>
				</>
			}
			empty={t("No contacts available.")}
		/>
	);
};
